import { useEffect, useMemo, useState, type FormEvent } from "react";
import { coffeeBeans, defaultOptionsForItem, drinkCategoryOrder, getOrderTotal, initialMenu, itemNeedsOptionDialog, validateItemOptions, validateOrderSelection } from "../shared/menu";
import type { CurrentUser, EventRecord, MenuItem, OrderOptions, OrderSelection } from "../shared/types";
import type { IchefCartLine, IchefSyncRun, IchefSyncServiceResult } from "../shared/ichef";

type View = "welcome" | "login" | "order" | "admin";
type AdminTab = "events" | "menu" | "orders" | "ichef" | "access";

const adminTabLabels: Record<AdminTab, string> = {
  events: "場次管理",
  menu: "菜單管理",
  orders: "訂單與統計",
  ichef: "送入 iCHEF",
  access: "權限管理",
};

interface AppConfig {
  authMode: "preview" | "uof";
  previewLocked: boolean;
}

interface ApiErrorShape {
  error?: { message?: string };
  message?: string;
  result?: { message?: string };
}

const previewEvent: EventRecord = {
  id: 1,
  title: "2026 八月月會",
  eventDate: "2026-08-31",
  deadlineAt: "2026-08-31T12:00:00+08:00",
  status: "open",
};

const previewUser: CurrentUser = {
  account: "PREVIEW",
  name: "驗收同仁",
  department: "封閉驗收模式",
  title: "不會保存資料",
  isAdmin: false,
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: init?.body instanceof FormData ? init.headers : { "Content-Type": "application/json", ...init?.headers },
  });
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("API 尚未啟用");
  }
  const payload: unknown = await response.json();
  if (!response.ok) {
    const errorPayload = typeof payload === "object" && payload !== null ? payload as ApiErrorShape : null;
    const message = errorPayload?.error?.message ?? errorPayload?.result?.message ?? errorPayload?.message;
    throw new Error(message ?? "系統暫時無法處理請求");
  }
  if (payload === null) {
    throw new Error("API 回應格式錯誤");
  }
  return payload as T;
}

function isAppConfig(value: unknown): value is AppConfig {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<AppConfig>;
  return (candidate.authMode === "preview" || candidate.authMode === "uof")
    && typeof candidate.previewLocked === "boolean";
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD", maximumFractionDigits: 0 }).format(value);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-TW", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Taipei" }).format(date);
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? "brand--compact" : ""}`}>
      <img src="/images/logo/cherry-logo.png" alt="櫻桃計畫 Cherry Espresso" />
    </div>
  );
}

function PreviewBanner() {
  return (
    <div className="preview-banner" role="status">
      <span className="preview-dot" />
      封閉驗收模式｜可完整試選，但不會登入 UOF、保存或送出任何資料
    </div>
  );
}

function AppLoading() {
  return <main className="app-loading" aria-live="polite"><BrandMark /><span className="loading-dot" /><p>正在準備點餐系統…</p></main>;
}

function Header({ config, user, view, onNavigate, onLogout }: { config: AppConfig; user: CurrentUser | null; view: View; onNavigate: (view: View) => void; onLogout: () => void }) {
  return (
    <>
      {config.previewLocked && <PreviewBanner />}
      <header className="topbar">
        <button className="brand-button" type="button" onClick={() => onNavigate(user ? "order" : config.previewLocked ? "welcome" : "login")} aria-label="回到首頁">
          <BrandMark compact />
        </button>
        <nav aria-label="主要導覽">
          {user && <button className={view === "order" ? "nav-active" : ""} onClick={() => onNavigate("order")}>我的點餐</button>}
          {user?.isAdmin && <button className={view === "admin" ? "nav-active" : ""} onClick={() => onNavigate("admin")}>管理後台</button>}
          {user && <button className="nav-user" onClick={onLogout}><span>{user.name}</span><small>{config.previewLocked ? "離開驗收" : "登出"}</small></button>}
        </nav>
      </header>
    </>
  );
}

function Welcome({ config, onStart, onAdminPreview }: { config: AppConfig; onStart: () => void; onAdminPreview: () => void }) {
  return (
    <main className="welcome-page">
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">CHERRY ESPRESSO · MONTHLY MEETING</p>
          <h1>今天，想吃點<br /><span>什麼好心情？</span></h1>
          <p className="hero-lead">一份主食，配上一杯剛剛好的飲品。三個步驟，完成你的月會餐點。</p>
          <div className="hero-actions">
            <button className="button button--primary button--large" onClick={onStart}>{config.previewLocked ? "進入封閉驗收" : "UOF 員工登入"}<span aria-hidden>→</span></button>
            {config.previewLocked && <button className="button button--ghost" onClick={onAdminPreview}>查看後台狀態</button>}
          </div>
          <div className="hero-notes">
            <span><b>1</b> 選主食</span><span><b>2</b> 搭飲品</span><span><b>3</b> 確認送出</span>
          </div>
        </div>
        <div className="hero-art" aria-hidden="true">
          <div className="hero-plate"><img src="/images/menu/main/田園蔬食餐盤.png" alt="" /></div>
          <div className="fruit fruit--orange" /><div className="fruit fruit--coral" /><div className="fruit fruit--green" />
          <div className="hero-stamp">月會限定<br /><strong>一人一份</strong></div>
        </div>
      </section>
      <section className="welcome-bottom">
        <article><span>01</span><h2>主食決定價格</h2><p>每份套餐包含一款主食與一杯飲品。</p></article>
        <article><span>02</span><h2>飲品自由搭配</h2><p>依品項選擇冰熱、甜度或咖啡豆。</p></article>
        <article><span>03</span><h2>截止前可修改</h2><p>正式開放後，登入即可查看與更新原訂單。</p></article>
      </section>
    </main>
  );
}

function Login({ onSuccess }: { onSuccess: (user: CurrentUser) => void }) {
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await api<{ user: CurrentUser }>("/api/auth/login", { method: "POST", body: JSON.stringify({ account, password }) });
      setPassword("");
      onSuccess(result.user);
    } catch (caught) {
      setPassword("");
      setError(caught instanceof Error ? caught.message : "登入失敗");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card">
        <BrandMark />
        <p className="eyebrow">EMPLOYEE SIGN IN</p>
        <h1>使用 UOF 帳號登入</h1>
        <form onSubmit={submit}>
          <label>員工編號<input inputMode="numeric" autoComplete="username" value={account} onChange={(event) => setAccount(event.target.value)} required /></label>
          <label>UOF 密碼<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="button button--primary button--wide" disabled={loading}>{loading ? "驗證中…" : "安全登入"}</button>
        </form>
      </section>
    </main>
  );
}

function ProductCard({ item, selected, disabled = false, onSelect }: { item: MenuItem; selected: boolean; disabled?: boolean; onSelect: () => void }) {
  const fixedTemperature = item.kind === "drink" && item.options.temperatures?.length === 1 ? `僅限${item.options.temperatures[0]}飲` : "";
  return (
    <button type="button" className={`product-card ${selected ? "is-selected" : ""}`} onClick={onSelect} aria-pressed={selected} disabled={disabled}>
      <div className="product-image"><img src={item.imageUrl} alt={item.name} loading="lazy" />{selected && <span className="selected-check">✓</span>}</div>
      <div className="product-info">
        <div className="product-title"><h3>{item.name}</h3>{item.price > 0 && <strong>{formatMoney(item.price)}</strong>}</div>
        {(item.dietaryTags.length > 0 || item.options.sauces || fixedTemperature || itemNeedsOptionDialog(item)) && <div className="tags">{item.dietaryTags.map((tag) => <span key={tag}>● {tag}</span>)}{item.options.sauces && <span>● 必選醬料</span>}{fixedTemperature && <span>● {fixedTemperature}</span>}{itemNeedsOptionDialog(item) && <span>● 點選客製</span>}</div>}
        {item.description && <p>{item.description}</p>}
      </div>
    </button>
  );
}

function Segmented<T extends string>({ label, values, selected, onChange }: { label: string; values: T[]; selected?: T; onChange: (value: T) => void }) {
  return (
    <fieldset className="option-fieldset">
      <legend>{label}<span>必選</span></legend>
      <div className="segmented">
        {values.map((value) => <button key={value} type="button" className={selected === value ? "is-active" : ""} onClick={() => onChange(value)}>{value}</button>)}
      </div>
    </fieldset>
  );
}

function DrinkOptions({ drink, options, onChange }: { drink: MenuItem; options: OrderOptions; onChange: (options: OrderOptions) => void }) {
  return (
    <div className="drink-options">
      {drink.options.temperatures && <Segmented label="冰熱" values={drink.options.temperatures} selected={options.temperature} onChange={(temperature) => onChange({ ...options, temperature })} />}
      {drink.options.sweetness && <Segmented label="甜度" values={drink.options.sweetness} selected={options.sweetness} onChange={(sweetness) => onChange({ ...options, sweetness })} />}
      {drink.options.coffeeBeans && (
        <fieldset className="option-fieldset bean-fieldset">
          <legend>選擇咖啡豆<span>必選</span></legend>
          <div className="bean-grid">
            {drink.options.coffeeBeans.map((bean) => (
              <button type="button" key={bean.id} className={`bean-card ${options.coffeeBeanId === bean.id ? "is-active" : ""}`} onClick={() => onChange({ ...options, coffeeBeanId: bean.id })}>
                <div><b>{bean.name}</b><em>{bean.roast}</em></div>
                <small>{bean.origin} · {bean.process}</small>
                <p>{bean.flavor}</p>
              </button>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

function MainOptions({ main, options, onChange }: { main: MenuItem; options: OrderOptions; onChange: (options: OrderOptions) => void }) {
  if (!main.options.sauces) return null;
  return <div className="drink-options"><Segmented label="醬料選擇" values={main.options.sauces} selected={options.sauce} onChange={(sauce) => onChange({ ...options, sauce })} /></div>;
}

function optionSummary(item: MenuItem, options: OrderOptions): string {
  const bean = item.options.coffeeBeans?.find((candidate) => candidate.id === options.coffeeBeanId)?.name;
  return [options.sauce ? `醬料：${options.sauce}` : "", options.temperature, options.sweetness, bean].filter(Boolean).join("・");
}

function OptionDialog({ item, initialOptions, onConfirm, onClose }: { item: MenuItem; initialOptions: OrderOptions; onConfirm: (options: OrderOptions) => void; onClose: () => void }) {
  const [draft, setDraft] = useState<OrderOptions>(() => ({ ...defaultOptionsForItem(item), ...initialOptions }));
  const errors = validateItemOptions(item, draft);
  return (
    <div className="modal-backdrop option-dialog-backdrop" role="presentation">
      <section className="modal option-dialog" role="dialog" aria-modal="true" aria-labelledby="option-dialog-title">
        <div className="modal-head"><div><p className="eyebrow">CUSTOMIZE YOUR ORDER</p><h2 id="option-dialog-title">選擇「{item.name}」口味</h2></div><button type="button" onClick={onClose} aria-label="關閉">×</button></div>
        <div className="option-dialog-product"><img src={item.imageUrl} alt="" /><div><b>{item.name}</b><p>{item.description || "完成選項後，才會加入你的餐點。"}</p></div></div>
        {item.kind === "main" ? <MainOptions main={item} options={draft} onChange={setDraft} /> : <DrinkOptions drink={item} options={draft} onChange={setDraft} />}
        {errors.length > 0 && <p className="option-dialog-hint">請完成所有必選項目</p>}
        <div className="modal-actions"><button type="button" className="button button--ghost" onClick={onClose}>取消</button><button type="button" className="button button--primary" disabled={errors.length > 0} onClick={() => onConfirm(draft)}>確認選擇</button></div>
      </section>
    </div>
  );
}

function OrderFlow({ config, user, event, menu, onAdmin }: { config: AppConfig; user: CurrentUser; event: EventRecord; menu: MenuItem[]; onAdmin: () => void }) {
  const [mainId, setMainId] = useState("");
  const [drinkId, setDrinkId] = useState("");
  const [mainOptions, setMainOptions] = useState<OrderOptions>({});
  const [drinkOptions, setDrinkOptions] = useState<OrderOptions>({});
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const mains = menu.filter((item) => item.kind === "main" && item.active);
  const drinks = menu.filter((item) => item.kind === "drink" && item.active);
  const selectedMain = mains.find((item) => item.id === mainId);
  const selectedDrink = drinks.find((item) => item.id === drinkId);
  const options: OrderOptions = { ...mainOptions, ...drinkOptions };
  const selection: OrderSelection = { mainItemId: mainId, drinkItemId: drinkId, options };
  const errors = validateOrderSelection(selection, menu);

  function chooseItem(item: MenuItem) {
    setMessage("");
    if (item.kind === "drink" && !selectedMain) return;
    if (itemNeedsOptionDialog(item)) {
      setEditingItem(item);
      return;
    }
    const defaults = defaultOptionsForItem(item);
    if (item.kind === "main") {
      setMainId(item.id);
      setMainOptions(defaults);
    } else {
      setDrinkId(item.id);
      setDrinkOptions(defaults);
    }
  }

  function confirmOptions(item: MenuItem, nextOptions: OrderOptions) {
    if (item.kind === "main") {
      setMainId(item.id);
      setMainOptions(nextOptions);
    } else {
      setDrinkId(item.id);
      setDrinkOptions(nextOptions);
    }
    setEditingItem(null);
  }

  async function submitOrder() {
    if (errors.length) return setMessage(errors[0]);
    setSaving(true);
    setMessage("");
    try {
      if (config.previewLocked) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
        setMessage("驗收完成：這份選擇不會送出或保存。");
      } else {
        await api("/api/orders/me", { method: "PUT", body: JSON.stringify(selection) });
        setMessage("餐點已送出！截止前仍可回來修改。");
      }
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "送出失敗");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="order-page">
      <section className="order-heading">
        <div><p className="eyebrow">{event.title}</p><h1>選好主食，再搭一杯飲品</h1><p>嗨，{user.name}・{user.department}　截止：{formatDateTime(event.deadlineAt)}</p></div>
        {user.isAdmin && <button className="button button--ghost" onClick={onAdmin}>管理後台</button>}
      </section>
      <div className="one-page-order">
        <div className="menu-stack">
          <section className="menu-section" aria-labelledby="main-section-title">
            <div className="section-title"><span>1</span><div><p className="eyebrow">MAIN COURSE</p><h2 id="main-section-title">選擇一份主食</h2></div>{selectedMain && <b>已選擇 ✓</b>}</div>
            <div className="product-grid product-grid--compact">{mains.map((item) => <ProductCard key={item.id} item={item} selected={mainId === item.id} onSelect={() => chooseItem(item)} />)}</div>
          </section>
          <section className={`menu-section ${selectedMain ? "" : "is-locked"}`} aria-labelledby="drink-section-title" aria-disabled={!selectedMain}>
            <div className="section-title"><span>2</span><div><p className="eyebrow">DRINK</p><h2 id="drink-section-title">選擇一杯飲品</h2></div>{selectedDrink && <b>已選擇 ✓</b>}</div>
            {!selectedMain && <div className="section-lock-note">請先選擇主食，再搭配飲品</div>}
            {drinkCategoryOrder.map((category) => {
              const categoryDrinks = drinks.filter((item) => item.category === category);
              if (!categoryDrinks.length) return null;
              return <div className="drink-group" key={category}><div className="drink-group-title"><h3>{category}</h3><span>{categoryDrinks.length} 款</span></div><div className="product-grid product-grid--compact product-grid--drinks">{categoryDrinks.map((item) => <ProductCard key={item.id} item={item} selected={drinkId === item.id} disabled={!selectedMain} onSelect={() => chooseItem(item)} />)}</div></div>;
            })}
          </section>
        </div>
        <aside className="order-sidebar" aria-label="餐點確認">
          <p className="eyebrow">YOUR ORDER</p><h2>你的餐點</h2>
          <div className={`selection-slot ${selectedMain ? "" : "is-empty"}`}>{selectedMain ? <><img src={selectedMain.imageUrl} alt="" /><div><small>主食</small><b>{selectedMain.name}</b><p>{optionSummary(selectedMain, mainOptions) || selectedMain.description}</p></div><strong>{formatMoney(selectedMain.price)}</strong></> : <><span>1</span><p>尚未選擇主食</p></>}</div>
          <div className={`selection-slot ${selectedDrink ? "" : "is-empty"}`}>{selectedDrink ? <><img src={selectedDrink.imageUrl} alt="" /><div><small>飲品</small><b>{selectedDrink.name}</b><p>{optionSummary(selectedDrink, drinkOptions)}</p></div><strong>套餐含</strong></> : <><span>2</span><p>尚未選擇飲品</p></>}</div>
          <div className="review-total"><span>套餐總計<small>飲品不另加價</small></span><strong>{formatMoney(getOrderTotal(selection, menu))}</strong></div>
          {message && <div className={message.includes("完成") || message.includes("送出") ? "form-success" : "form-error"} role="status">{message}</div>}
          <button className="button button--primary button--wide button--large" disabled={saving || errors.length > 0} onClick={submitOrder}>{saving ? "處理中…" : config.previewLocked ? "完成驗收（不會送出）" : "確認送出餐點"}</button>
          <p className="sidebar-note">{config.previewLocked ? "目前為封閉驗收模式，不會保存或送出資料。" : "截止前可回來修改或取消訂單。"}</p>
        </aside>
      </div>
      {editingItem && <OptionDialog key={editingItem.id} item={editingItem} initialOptions={editingItem.kind === "main" && editingItem.id === mainId ? mainOptions : editingItem.kind === "drink" && editingItem.id === drinkId ? drinkOptions : {}} onClose={() => setEditingItem(null)} onConfirm={(nextOptions) => confirmOptions(editingItem, nextOptions)} />}
    </main>
  );
}

interface AdminOrder {
  id: string; account: string; name: string; department: string; title: string; status: "active" | "cancelled";
  main_item_id: string; drink_item_id: string; main_snapshot_json: string; drink_snapshot_json: string;
  options_json: string; total_price: number; updated_at: number;
}

interface AdminAccess {
  account: string;
  name: string | null;
  department: string | null;
  title: string | null;
  grantedBy: string;
  createdAt: number;
  isSystem: boolean;
  canRemove: boolean;
}

interface IchefPreviewData {
  event: EventRecord;
  tables: readonly string[];
  selectedTable: string;
  orderCount: number;
  cherryTotal: number;
  expectedIchefTotal: number;
  priceDifference: number;
  orderDigest: string;
  lines: IchefCartLine[];
  blockers: string[];
  warnings: string[];
  canValidate: boolean;
  canSubmit: boolean;
  latestValidation: IchefSyncRun | null;
  committedRun: IchefSyncRun | null;
  history: IchefSyncRun[];
}

interface IchefSyncApiResponse {
  pending: boolean;
  runId: string;
  message?: string;
  result?: IchefSyncServiceResult;
  preview?: IchefPreviewData;
}

function formatRunTime(value: number | null): string {
  return value ? formatDateTime(new Date(value * 1000).toISOString()) : "處理中";
}

function IchefSyncPanel({ eventId }: { eventId: number }) {
  const [tableName, setTableName] = useState("A1");
  const [preview, setPreview] = useState<IchefPreviewData | null>(null);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<"validate" | "live" | null>(null);
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmTable, setConfirmTable] = useState("");

  async function load(nextTable = tableName) {
    setLoading(true);
    try {
      const data = await api<{ preview: IchefPreviewData; liveEnabled: boolean }>(`/api/admin/ichef?eventId=${eventId}&table=${encodeURIComponent(nextTable)}`);
      setPreview(data.preview);
      setLiveEnabled(data.liveEnabled);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "無法讀取 iCHEF 同步狀態");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setMessage(""); void load(); }, [eventId, tableName]);

  async function run(mode: "validate" | "live") {
    setRunning(mode);
    setMessage("");
    try {
      let response = await api<IchefSyncApiResponse>("/api/admin/ichef/sync", {
        method: "POST",
        body: JSON.stringify({ eventId, tableName, mode, ...(mode === "live" ? { confirmTable } : {}) }),
      });
      for (let step = 0; response.pending && step < 100; step += 1) {
        setMessage(response.message ?? "正在操作 iCHEF…");
        response = await api<IchefSyncApiResponse>(`/api/admin/ichef/sync/${encodeURIComponent(response.runId)}/continue`, { method: "POST" });
      }
      if (response.pending) throw new Error("iCHEF 處理步驟過多，已停止自動操作，請重新整理後確認狀態");
      if (!response.result || !response.preview) throw new Error("iCHEF 完成回應不完整，請重新整理後確認狀態");
      setPreview(response.preview);
      setMessage(response.result.message);
      setConfirming(false);
      setConfirmTable("");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "iCHEF 同步失敗");
      await load();
    } finally {
      setRunning(null);
    }
  }

  if (loading && !preview) return <section className="panel ichef-panel"><p className="ichef-loading">正在整理本場次訂單與 iCHEF 對應品項…</p></section>;
  if (!preview) return <section className="panel ichef-panel"><div className="form-error">{message || "無法讀取 iCHEF 同步狀態"}</div><button className="button button--ghost" onClick={() => void load()}>重新整理</button></section>;
  const groupedTables = [...new Set(preview.tables.map((table) => table[0]))];
  return (
    <section className="ichef-layout">
      <div className="panel ichef-panel">
        <div className="panel-head"><div><p className="eyebrow">ICHEF CLOUD RESTAURANT</p><h2>桌號與送單確認</h2><p>系統會建立一張桌單，彙整本場次所有有效訂單。</p></div><span className={`status status--${preview.committedRun ? "closed" : "draft"}`}>{preview.committedRun ? "已鎖定" : "尚未送單"}</span></div>
        <label className="ichef-table-select">iCHEF 桌號<select value={tableName} disabled={Boolean(preview.committedRun) || running !== null} onChange={(event) => setTableName(event.target.value)}>{groupedTables.map((prefix) => <optgroup label={`${prefix} 區`} key={prefix}>{preview.tables.filter((table) => table.startsWith(prefix)).map((table) => <option value={table} key={table}>{table}</option>)}</optgroup>)}</select></label>
        <div className="stats-grid ichef-stats"><article><small>有效訂單</small><strong>{preview.orderCount}</strong><span>份套餐</span></article><article><small>月會平台金額</small><strong>{formatMoney(preview.cherryTotal)}</strong><span>依主食補助價</span></article><article><small>iCHEF 預估金額</small><strong>{formatMoney(preview.expectedIchefTotal)}</strong><span>差額 {formatMoney(preview.priceDifference)}</span></article></div>
        {preview.blockers.length > 0 && <div className="ichef-alert ichef-alert--error"><b>目前無法送單</b>{preview.blockers.map((warning) => <p key={warning}>• {warning}</p>)}</div>}
        <div className="ichef-alert"><b>送單前注意</b>{preview.warnings.map((warning) => <p key={warning}>• {warning}</p>)}</div>
        {preview.event.status !== "closed" && <div className="ichef-alert ichef-alert--info"><b>正式送單前請先關閉場次</b><p>目前場次狀態是「{preview.event.status === "open" ? "開放" : "草稿"}」。可先驗證到 iCHEF 結帳前；確認不再收單後，到「場次管理」改為關閉。</p></div>}
        {preview.latestValidation && <div className="ichef-validation"><span>✓</span><div><b>最近一次結帳前驗證成功</b><p>{preview.latestValidation.tableName}・{formatRunTime(preview.latestValidation.completedAt)}・{formatMoney(preview.latestValidation.observedIchefTotal ?? preview.latestValidation.expectedIchefTotal)}</p></div></div>}
        {preview.committedRun && <div className={`ichef-validation ${preview.committedRun.status === "needs_review" || preview.committedRun.status === "processing" ? "ichef-validation--review" : ""}`}><span>{preview.committedRun.status === "succeeded" ? "✓" : "!"}</span><div><b>{preview.committedRun.status === "succeeded" ? "已送入 iCHEF" : "需要人工確認 iCHEF POS"}</b><p>{preview.committedRun.tableName}・{formatRunTime(preview.committedRun.completedAt)}・{preview.committedRun.message}</p></div></div>}
        {message && <div className="admin-notice">{message}<button onClick={() => setMessage("")}>×</button></div>}
        <div className="ichef-actions"><button className="button button--ghost" disabled={!preview.canValidate || running !== null} onClick={() => void run("validate")}>{running === "validate" ? "正在操作 iCHEF…" : "先驗證到結帳前"}</button><button className="button button--primary" disabled={!liveEnabled || !preview.canSubmit || running !== null} onClick={() => { setConfirmTable(""); setConfirming(true); }}>正式送入 iCHEF</button></div>
        {!preview.canSubmit && !preview.committedRun && <small className="ichef-action-hint">正式送單需符合：場次已關閉、同桌號與同批訂單於 30 分鐘內驗證成功。</small>}
      </div>
      <div className="panel ichef-lines"><div className="panel-head"><div><h2>將送入的品項</h2><p>同品項、同客製選項已合併數量。</p></div><span className="admin-count">{preview.lines.reduce((sum, line) => sum + line.quantity, 0)} 項</span></div><div className="ichef-line-list">{preview.lines.map((line) => <article key={`${line.itemId}-${line.selections.join("-")}`}><div><b>{line.menuName.split(" ")[0]}</b><p>{line.selections.length ? line.selections.join("・") : "無客製選項"}</p></div><span>× {line.quantity}</span><strong>{formatMoney(line.unitPrice * line.quantity)}</strong></article>)}</div></div>
      {confirming && <div className="modal-backdrop" role="presentation"><section className="modal modal--confirm" role="dialog" aria-modal="true" aria-labelledby="ichef-confirm-title"><div className="confirm-icon" aria-hidden="true">!</div><p className="eyebrow">FINAL ICHEF SUBMISSION</p><h2 id="ichef-confirm-title">正式送入桌號 {tableName}？</h2><p>這會在 iCHEF 建立真實訂單，送出後本場次訂單將鎖定。預估 iCHEF 金額為 <b>{formatMoney(preview.expectedIchefTotal)}</b>。</p><label className="ichef-confirm-input">請輸入桌號「{tableName}」確認<input autoFocus value={confirmTable} onChange={(event) => setConfirmTable(event.target.value.toUpperCase())} /></label><div className="modal-actions"><button className="button button--ghost" disabled={running !== null} onClick={() => setConfirming(false)}>返回</button><button className="button button--danger" disabled={confirmTable !== tableName || running !== null} onClick={() => void run("live")}>{running === "live" ? "正在送入 iCHEF…" : "確認正式送單"}</button></div></section></div>}
    </section>
  );
}

function AdminLocked() {
  return <main className="admin-page"><section className="locked-panel"><span>🔒</span><p className="eyebrow">ADMIN PREVIEW</p><h1>管理後台尚未開放</h1><p>UOF HTTPS 驗證與組織查詢通過整合測試後，員工編號 251102 可在這裡管理場次、菜單、圖片、訂單與匯出。</p><div className="locked-features"><b>場次與截止時間</b><b>完整菜單編輯</b><b>訂單統計與 CSV</b><b>管理員稽核紀錄</b></div></section></main>;
}

function MenuEditor({ item, eventId, onSaved, onClose }: { item: MenuItem | null; eventId: number; onSaved: () => void; onClose: () => void }) {
  const blank: MenuItem = { id: "", kind: "main", category: "主食", name: "", price: 0, description: "", dietaryTags: [], imageUrl: "", options: {}, active: true, sortOrder: 999 };
  const [draft, setDraft] = useState<MenuItem>(item ?? blank);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function upload(file: File) {
    const form = new FormData(); form.append("image", file);
    const result = await api<{ imageUrl: string }>("/api/admin/images", { method: "POST", body: form });
    setDraft((value) => ({ ...value, imageUrl: result.imageUrl }));
  }

  async function save(event: FormEvent) {
    event.preventDefault(); setSaving(true); setError("");
    try {
      const path = item ? `/api/admin/menu/${item.id}` : "/api/admin/menu";
      await api(path, { method: item ? "PUT" : "POST", body: JSON.stringify({ eventId, item: draft }) });
      onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "儲存失敗"); } finally { setSaving(false); }
  }

  function toggleOption(kind: "sauces" | "temperatures" | "sweetness", value: string) {
    const current = (draft.options[kind] ?? []) as string[];
    const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];
    setDraft({ ...draft, options: { ...draft.options, [kind]: next } });
  }

  return (
    <div className="modal-backdrop" role="presentation"><section className="modal" role="dialog" aria-modal="true" aria-label="編輯菜單品項"><div className="modal-head"><h2>{item ? "編輯品項" : "新增品項"}</h2><button onClick={onClose} aria-label="關閉">×</button></div>
      <form className="admin-form" onSubmit={save}>
        <div className="form-row"><label>類型<select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as MenuItem["kind"], category: e.target.value === "main" ? "主食" : "純粹茶飲" })}><option value="main">主食</option><option value="drink">飲品</option></select></label><label>分類<input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} required /></label></div>
        <div className="form-row"><label>品項名稱<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required /></label><label>價格<input type="number" min="0" value={draft.price} onChange={(e) => setDraft({ ...draft, price: Number(e.target.value) })} required /></label></div>
        <label>內容說明<textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} /></label>
        <label>標籤<input value={draft.dietaryTags.join("、")} onChange={(e) => setDraft({ ...draft, dietaryTags: e.target.value.split("、").map((value) => value.trim()).filter(Boolean) })} placeholder="例如：蛋奶素" /></label>
        <div className="form-row"><label>圖片網址<input value={draft.imageUrl} onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })} required /></label><label className="file-label">上傳新圖片<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} /></label></div>
        {draft.kind === "main" && <div className="option-editor"><b>主食客製規則</b><div><span>醬料：</span>{["起司醬", "松露醬"].map((value) => <label key={value}><input type="checkbox" checked={draft.options.sauces?.includes(value as "起司醬" | "松露醬") ?? false} onChange={() => toggleOption("sauces", value)} />{value}</label>)}</div></div>}
        {draft.kind === "drink" && <div className="option-editor"><b>飲品客製規則</b><div><span>冰熱：</span>{["冰", "熱"].map((value) => <label key={value}><input type="checkbox" checked={draft.options.temperatures?.includes(value as "冰" | "熱") ?? false} onChange={() => toggleOption("temperatures", value)} />{value}</label>)}</div><div><span>甜度：</span>{["無糖", "微糖"].map((value) => <label key={value}><input type="checkbox" checked={draft.options.sweetness?.includes(value as "無糖" | "微糖") ?? false} onChange={() => toggleOption("sweetness", value)} />{value}</label>)}</div><label><input type="checkbox" checked={Boolean(draft.options.coffeeBeans)} onChange={(e) => setDraft({ ...draft, options: { ...draft.options, coffeeBeans: e.target.checked ? coffeeBeans : undefined } })} />需要選擇六款咖啡豆</label></div>}
        <div className="form-row"><label>排序<input type="number" value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })} /></label><label className="switch-label"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />上架中</label></div>
        {error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button button--ghost" onClick={onClose}>取消</button><button className="button button--primary" disabled={saving}>{saving ? "儲存中…" : "儲存品項"}</button></div>
      </form></section></div>
  );
}

function EventEditor({ record, onSaved, onClose }: { record: EventRecord; onSaved: (record: EventRecord) => Promise<void>; onClose: () => void }) {
  const [draft, setDraft] = useState(record);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  async function save(event: FormEvent) {
    event.preventDefault(); setSaving(true); setError("");
    try { await onSaved(draft); onClose(); } catch (caught) { setError(caught instanceof Error ? caught.message : "儲存失敗"); } finally { setSaving(false); }
  }
  const localDeadline = new Date(draft.deadlineAt).toLocaleString("sv-SE", { timeZone: "Asia/Taipei" }).slice(0, 16).replace(" ", "T");
  return <div className="modal-backdrop" role="presentation"><section className="modal modal--small" role="dialog" aria-modal="true" aria-label="編輯場次"><div className="modal-head"><h2>編輯場次</h2><button onClick={onClose} aria-label="關閉">×</button></div><form className="admin-form" onSubmit={save}><label>場次名稱<input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required /></label><div className="form-row"><label>月會日期<input type="date" value={draft.eventDate} onChange={(e) => setDraft({ ...draft, eventDate: e.target.value })} required /></label><label>狀態<select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as EventRecord["status"] })}><option value="draft">草稿</option><option value="open">開放</option><option value="closed">關閉</option></select></label></div><label>截止時間<input type="datetime-local" value={localDeadline} onChange={(e) => setDraft({ ...draft, deadlineAt: new Date(e.target.value).toISOString() })} required /></label>{error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button button--ghost" onClick={onClose}>取消</button><button className="button button--primary" disabled={saving}>{saving ? "儲存中…" : "儲存場次"}</button></div></form></section></div>;
}

function AdminOrderEditor({ order, menu, onSaved, onClose }: { order: AdminOrder; menu: MenuItem[]; onSaved: () => Promise<void>; onClose: () => void }) {
  const [mainId, setMainId] = useState(order.main_item_id);
  const [drinkId, setDrinkId] = useState(order.drink_item_id);
  const [options, setOptions] = useState<OrderOptions>(() => JSON.parse(order.options_json) as OrderOptions);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const mains = menu.filter((item) => item.kind === "main" && item.active);
  const drinks = menu.filter((item) => item.kind === "drink" && item.active);
  const main = mains.find((item) => item.id === mainId);
  const drink = drinks.find((item) => item.id === drinkId);
  const selection = { mainItemId: mainId, drinkItemId: drinkId, options };
  const validation = validateOrderSelection(selection, menu);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (validation.length) return setError(validation[0]);
    setSaving(true); setError("");
    try { await api(`/api/admin/orders/${order.id}`, { method: "PUT", body: JSON.stringify(selection) }); await onSaved(); onClose(); } catch (caught) { setError(caught instanceof Error ? caught.message : "代改失敗"); } finally { setSaving(false); }
  }

  return <div className="modal-backdrop" role="presentation"><section className="modal modal--small" role="dialog" aria-modal="true" aria-label="管理員代改訂單"><div className="modal-head"><div><p className="eyebrow">ADMIN ORDER EDIT</p><h2>代改 {order.name} 的訂單</h2></div><button onClick={onClose} aria-label="關閉">×</button></div><form className="admin-form" onSubmit={save}><label>主食<select value={mainId} onChange={(e) => { const next = mains.find((item) => item.id === e.target.value); setMainId(e.target.value); setOptions((current) => { const { sauce: _previousSauce, ...remaining } = current; return { ...remaining, ...(next?.options.sauces?.length === 1 ? { sauce: next.options.sauces[0] } : {}) }; }); }}>{mains.map((item) => <option value={item.id} key={item.id}>{item.name}・{formatMoney(item.price)}</option>)}</select></label>{main && <MainOptions main={main} options={options} onChange={setOptions} />}<label>飲品<select value={drinkId} onChange={(e) => { const next = drinks.find((item) => item.id === e.target.value); setDrinkId(e.target.value); setOptions((current) => ({ ...(current.sauce ? { sauce: current.sauce } : {}), ...(next?.options.temperatures?.length === 1 ? { temperature: next.options.temperatures[0] } : {}) })); }}>{drinks.map((item) => <option value={item.id} key={item.id}>{item.category}・{item.name}</option>)}</select></label>{drink && <DrinkOptions drink={drink} options={options} onChange={setOptions} />}{error && <div className="form-error">{error}</div>}<div className="review-total"><span>更新後總價</span><strong>{formatMoney(getOrderTotal(selection, menu))}</strong></div><div className="modal-actions"><button type="button" className="button button--ghost" onClick={onClose}>取消</button><button className="button button--primary" disabled={saving || validation.length > 0}>{saving ? "更新中…" : "確認代改"}</button></div></form></section></div>;
}

function RemoveOrderDialog({ order, onRemoved, onClose }: { order: AdminOrder; onRemoved: () => Promise<void>; onClose: () => void }) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  async function remove() {
    setRemoving(true);
    setError("");
    try {
      await api(`/api/admin/orders/${order.id}/remove`, { method: "DELETE" });
      await onRemoved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "移除失敗");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal modal--confirm" role="dialog" aria-modal="true" aria-labelledby="remove-order-title">
        <div className="confirm-icon" aria-hidden="true">−</div>
        <p className="eyebrow">REMOVE CANCELLED ORDER</p>
        <h2 id="remove-order-title">移除 {order.name} 的訂單？</h2>
        <p>移除後將不再顯示於訂單列表與 CSV，但系統會保留稽核記錄。</p>
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="modal-actions"><button type="button" className="button button--ghost" disabled={removing} onClick={onClose}>返回</button><button type="button" className="button button--danger" disabled={removing} onClick={() => void remove()}>{removing ? "移除中…" : "確認移除"}</button></div>
      </section>
    </div>
  );
}

function RemoveAdminAccessDialog({ admin, onRemoved, onClose }: { admin: AdminAccess; onRemoved: () => Promise<void>; onClose: () => void }) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  async function remove() {
    setRemoving(true);
    setError("");
    try {
      await api(`/api/admin/access/${encodeURIComponent(admin.account)}`, { method: "DELETE" });
      await onRemoved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "移除權限失敗");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal modal--confirm" role="dialog" aria-modal="true" aria-labelledby="remove-admin-title">
        <div className="confirm-icon" aria-hidden="true">−</div>
        <p className="eyebrow">REMOVE ADMIN ACCESS</p>
        <h2 id="remove-admin-title">移除 {admin.name ?? admin.account} 的後台權限？</h2>
        <p>移除後，這位員工將無法再查看或操作管理後台。</p>
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="modal-actions"><button type="button" className="button button--ghost" disabled={removing} onClick={onClose}>返回</button><button type="button" className="button button--danger" disabled={removing} onClick={() => void remove()}>{removing ? "移除中…" : "確認移除權限"}</button></div>
      </section>
    </div>
  );
}

function AdminDashboard({ initialEvent, initialMenuItems }: { initialEvent: EventRecord; initialMenuItems: MenuItem[] }) {
  const [tab, setTab] = useState<AdminTab>("events");
  const [events, setEvents] = useState<EventRecord[]>([initialEvent]);
  const [eventId, setEventId] = useState(initialEvent.id);
  const [menu, setMenu] = useState(initialMenuItems);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [admins, setAdmins] = useState<AdminAccess[]>([]);
  const [editing, setEditing] = useState<MenuItem | null | "new">(null);
  const [editingEvent, setEditingEvent] = useState<EventRecord | null>(null);
  const [editingOrder, setEditingOrder] = useState<AdminOrder | null>(null);
  const [removingOrder, setRemovingOrder] = useState<AdminOrder | null>(null);
  const [removingAdmin, setRemovingAdmin] = useState<AdminAccess | null>(null);
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");

  async function loadEvents() { const data = await api<{ events: EventRecord[] }>("/api/admin/events"); setEvents(data.events); }
  async function loadMenu() { const data = await api<{ items: MenuItem[] }>(`/api/admin/menu?eventId=${eventId}`); setMenu(data.items); }
  async function loadOrders() { const data = await api<{ orders: AdminOrder[] }>(`/api/admin/orders?eventId=${eventId}&search=${encodeURIComponent(search)}`); setOrders(data.orders); }
  async function loadAdmins() { const data = await api<{ admins: AdminAccess[] }>("/api/admin/access"); setAdmins(data.admins); }
  useEffect(() => { void loadEvents().catch((error: Error) => setNotice(error.message)); }, []);
  useEffect(() => { if (tab === "menu") void loadMenu().catch((error: Error) => setNotice(error.message)); if (tab === "orders") { void loadOrders().catch((error: Error) => setNotice(error.message)); void loadMenu().catch((error: Error) => setNotice(error.message)); } if (tab === "access") void loadAdmins().catch((error: Error) => setNotice(error.message)); }, [tab, eventId]);

  const activeOrders = orders.filter((order) => order.status === "active");
  const mainCounts = useMemo(() => {
    const result = new Map<string, number>();
    activeOrders.forEach((order) => { const name = (JSON.parse(order.main_snapshot_json) as { name: string }).name; result.set(name, (result.get(name) ?? 0) + 1); });
    return [...result.entries()].sort((a, b) => b[1] - a[1]);
  }, [orders]);

  async function createEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    const cloneFrom = Number(form.get("cloneFrom"));
    const payload = { title: form.get("title"), eventDate: form.get("eventDate"), deadlineAt: new Date(String(form.get("deadlineAt"))).toISOString() };
    await api(cloneFrom ? `/api/admin/events/${cloneFrom}/clone` : "/api/admin/events", { method: "POST", body: JSON.stringify(payload) });
    event.currentTarget.reset(); setNotice(cloneFrom ? "已複製場次與完整菜單" : "新場次已建立為草稿"); await loadEvents();
  }

  async function updateEvent(record: EventRecord, patch: Partial<EventRecord>) {
    await api(`/api/admin/events/${record.id}`, { method: "PUT", body: JSON.stringify({ ...record, ...patch }) });
    setNotice("場次設定已更新"); await loadEvents();
  }

  async function addAdmin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await api("/api/admin/access", { method: "POST", body: JSON.stringify({ account: data.get("account") }) });
    form.reset();
    setNotice("後台權限已新增，對方重新整理或下次登入後即可看到管理後台。");
    await loadAdmins();
  }

  return (
    <main className="admin-page">
      <section className="admin-heading"><div><p className="eyebrow">SYSTEM ADMINISTRATION</p><h1>月會點餐管理後台</h1><p>管理場次、菜單、員工訂單、iCHEF 送單與後台權限。</p></div>{tab !== "access" && <select value={eventId} onChange={(e) => setEventId(Number(e.target.value))}>{events.map((event) => <option value={event.id} key={event.id}>{event.title}</option>)}</select>}</section>
      <div className="admin-tabs">{(Object.keys(adminTabLabels) as AdminTab[]).map((name) => <button key={name} className={tab === name ? "is-active" : ""} onClick={() => setTab(name)}>{adminTabLabels[name]}</button>)}</div>
      {notice && <div className="admin-notice">{notice}<button onClick={() => setNotice("")}>×</button></div>}

      {tab === "events" && <section className="admin-grid"><div className="panel"><div className="panel-head"><h2>所有場次</h2></div><div className="event-list">{events.map((record) => <article key={record.id}><div><span className={`status status--${record.status}`}>{record.status}</span><h3>{record.title}</h3><p>{record.eventDate}・截止 {formatDateTime(record.deadlineAt)}</p></div><div className="event-actions"><select value={record.status} onChange={(e) => void updateEvent(record, { status: e.target.value as EventRecord["status"] })}><option value="draft">草稿</option><option value="open">開放</option><option value="closed">關閉</option></select><button className="button button--ghost" onClick={() => setEditingEvent(record)}>設定</button></div></article>)}</div></div><div className="panel"><div className="panel-head"><h2>建立新場次</h2></div><form className="admin-form" onSubmit={(event) => void createEvent(event)}><label>場次名稱<input name="title" placeholder="例如：2026 九月月會" required /></label><label>月會日期<input name="eventDate" type="date" required /></label><label>截止時間<input name="deadlineAt" type="datetime-local" required /></label><label>菜單來源<select name="cloneFrom" defaultValue={eventId}><option value="0">建立空白菜單</option>{events.map((record) => <option value={record.id} key={record.id}>複製「{record.title}」</option>)}</select></label><button className="button button--primary">建立草稿</button></form></div></section>}

      {tab === "menu" && <section className="panel"><div className="panel-head"><div><h2>本場次菜單</h2><p>{menu.length} 個品項，可編輯內容與上下架。</p></div><button className="button button--primary" onClick={() => setEditing("new")}>＋ 新增品項</button></div><div className="menu-table">{menu.map((item) => <button key={item.id} onClick={() => setEditing(item)}><img src={item.imageUrl} alt="" /><span><b>{item.name}</b><small>{item.category}・{item.active ? "上架" : "下架"}</small></span><strong>{item.kind === "main" ? formatMoney(item.price) : "套餐含"}</strong><em>編輯</em></button>)}</div></section>}

      {tab === "orders" && <section><div className="stats-grid"><article><small>有效訂單</small><strong>{activeOrders.length}</strong><span>份套餐</span></article><article><small>預估餐費</small><strong>{formatMoney(activeOrders.reduce((sum, order) => sum + order.total_price, 0))}</strong><span>依主食價格</span></article><article><small>最熱門主食</small><strong className="stats-name">{mainCounts[0]?.[0] ?? "尚無訂單"}</strong><span>{mainCounts[0]?.[1] ?? 0} 份</span></article></div><div className="panel"><div className="panel-head"><div><h2>訂單明細</h2><p>可搜尋、代改、取消、移除與匯出。</p></div><div className="order-tools"><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="姓名或員工編號" /><button className="button button--ghost" onClick={() => void loadOrders()}>搜尋</button><a className="button button--primary" href={`/api/admin/orders/export?eventId=${eventId}`}>匯出 CSV</a></div></div><div className="orders-table"><div className="orders-row orders-head"><span>員工</span><span>主食／飲品</span><span>總價</span><span>狀態</span><span>操作</span></div>{orders.map((order) => { const main = JSON.parse(order.main_snapshot_json) as MenuItem; const drink = JSON.parse(order.drink_snapshot_json) as MenuItem; return <div className="orders-row" key={order.id}><span data-label="員工"><b>{order.name}</b><small>{order.account}・{order.department}</small></span><span data-label="主食／飲品"><b>{main.name}</b><small>{drink.name}</small></span><span data-label="總價">{formatMoney(order.total_price)}</span><span data-label="狀態"><i className={`status status--${order.status === "active" ? "open" : "closed"}`}>{order.status === "active" ? "有效" : "已取消"}</i></span><span className="row-actions" data-label="操作">{order.status === "active" ? <><button className="link-button" onClick={() => setEditingOrder(order)}>代改</button><button className="link-button link-danger" onClick={async () => { await api(`/api/admin/orders/${order.id}`, { method: "DELETE" }); setNotice("訂單已取消，如不需保留在列表可再點選移除。"); await loadOrders(); }}>取消</button></> : <button className="link-button link-remove" onClick={() => setRemovingOrder(order)}>移除</button>}</span></div>; })}</div></div></section>}
      {tab === "ichef" && <IchefSyncPanel eventId={eventId} />}
      {tab === "access" && <section className="admin-grid admin-access-grid"><div className="panel"><div className="panel-head"><div><h2>可使用後台的人員</h2><p>權限由伺服器驗證，移除後會立即停止後台 API 存取。</p></div><span className="admin-count">{admins.length} 人</span></div><div className="admin-access-list">{admins.map((admin) => <article key={admin.account}><div className="admin-avatar" aria-hidden="true">{(admin.name ?? admin.account).slice(0, 1)}</div><div className="admin-identity"><b>{admin.name ?? "尚未登入同步姓名"}</b><span>{admin.account}{admin.department ? `・${admin.department}` : ""}{admin.title ? `・${admin.title}` : ""}</span><small>{admin.isSystem ? "系統管理員" : `由 ${admin.grantedBy} 授權`}</small></div><div className="admin-access-actions"><i className={`status ${admin.isSystem ? "status--open" : "status--draft"}`}>{admin.isSystem ? "固定權限" : "後台管理員"}</i>{admin.canRemove && <button className="link-button link-danger" onClick={() => setRemovingAdmin(admin)}>移除權限</button>}</div></article>)}</div></div><div className="panel access-add-panel"><div className="panel-head"><div><h2>新增後台管理員</h2><p>可先輸入員工編號；對方登入後會自動補上姓名與主要部門。</p></div></div><form className="admin-form" onSubmit={(event) => void addAdmin(event).catch((error: Error) => setNotice(error.message))}><label>員工編號<input name="account" inputMode="numeric" pattern="[0-9]{1,20}" placeholder="例如：251102" autoComplete="off" required /></label><button className="button button--primary">新增後台權限</button></form><div className="access-security-note"><b>權限說明</b><p>所有後台管理員都能管理場次、菜單、訂單及權限名單。系統管理員 251102 的固定權限無法從此處移除。</p></div></div></section>}
      {editing && <MenuEditor item={editing === "new" ? null : editing} eventId={eventId} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await loadMenu(); setNotice("菜單品項已儲存"); }} />}
      {editingEvent && <EventEditor record={editingEvent} onClose={() => setEditingEvent(null)} onSaved={async (record) => { await updateEvent(editingEvent, record); }} />}
      {editingOrder && <AdminOrderEditor order={editingOrder} menu={menu} onClose={() => setEditingOrder(null)} onSaved={async () => { await loadOrders(); setNotice("訂單已由管理員更新並記錄稽核"); }} />}
      {removingOrder && <RemoveOrderDialog order={removingOrder} onClose={() => setRemovingOrder(null)} onRemoved={async () => { await loadOrders(); setNotice("已從訂單列表移除，稽核記錄已保留。"); }} />}
      {removingAdmin && <RemoveAdminAccessDialog admin={removingAdmin} onClose={() => setRemovingAdmin(null)} onRemoved={async () => { await loadAdmins(); setNotice("後台權限已移除，稽核記錄已保留。"); }} />}
    </main>
  );
}

export default function App() {
  const [config, setConfig] = useState<AppConfig>({ authMode: "preview", previewLocked: true });
  const [view, setView] = useState<View>("welcome");
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [event, setEvent] = useState<EventRecord>(previewEvent);
  const [menu, setMenu] = useState<MenuItem[]>(initialMenu);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const configPayload: unknown = await api("/api/config");
        if (!isAppConfig(configPayload)) throw new Error("API 設定格式錯誤");
        const loadedConfig = configPayload;
        setConfig(loadedConfig);
        const menuPayload = await api<{ event: EventRecord | null; items: MenuItem[] }>("/api/menu");
        if (menuPayload.event) setEvent(menuPayload.event);
        if (menuPayload.items.length) setMenu(menuPayload.items);
        if (!loadedConfig.previewLocked) {
          const auth = await api<{ authenticated: boolean; user: CurrentUser | null }>("/api/auth/me");
          if (auth.authenticated && auth.user) { setUser(auth.user); setView("order"); }
          else setView("login");
        } else {
          setView("welcome");
        }
      } catch {
        setConfig({ authMode: "preview", previewLocked: true });
        setView("welcome");
      } finally {
        setInitializing(false);
      }
    })();
  }, []);

  function start() {
    if (config.previewLocked) { setUser(previewUser); setView("order"); }
    else setView("login");
  }

  async function logout() {
    if (!config.previewLocked) await api("/api/auth/logout", { method: "POST" }).catch(() => undefined);
    setUser(null); setView(config.previewLocked ? "welcome" : "login");
  }

  if (initializing) return <AppLoading />;

  return (
    <div className="app-shell">
      <Header config={config} user={user} view={view} onNavigate={setView} onLogout={() => void logout()} />
      {view === "welcome" && <Welcome config={config} onStart={start} onAdminPreview={() => setView("admin")} />}
      {view === "login" && <Login onSuccess={(nextUser) => { setUser(nextUser); setView("order"); }} />}
      {view === "order" && user && <OrderFlow config={config} user={user} event={event} menu={menu} onAdmin={() => setView("admin")} />}
      {view === "admin" && (config.previewLocked ? <AdminLocked /> : user?.isAdmin ? <AdminDashboard initialEvent={event} initialMenuItems={menu} /> : <Login onSuccess={(nextUser) => { setUser(nextUser); setView(nextUser.isAdmin ? "admin" : "order"); }} />)}
    </div>
  );
}
